import React, { useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { Alert, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material'
import { mapUpload, playerRequest } from '../service'
import { canRerun, hasGameAppearance, latestRuns } from '../service/aiHighlights'
import { gamePartsFor, isGameVideo, partNumber, withoutReplacedRetries } from '../service/gameParts'
import { uploadStatusLabel } from '../components/upload/UploadProcessingStatus'

export default function Game () {
  const { gameId } = useParams()
  const [game, setGame] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  // One ID per requested re-run, kept until it succeeds so a retry cannot start a second run.
  const rerunIds = useRef({})
  useEffect(() => {
    let active = true
    setGame(null); setError('')
    const load = () => playerRequest(`/games/${gameId}`).then(data => { if (active) setGame(data) }).catch(e => { if (active) setError(e.message) })
    load()
    const timer = window.setInterval(load, 15000)
    return () => { active = false; window.clearInterval(timer) }
  }, [gameId])
  const startAnalysis = async (partId, rerun = false) => {
    setBusy(true); setError('')
    try {
      if (rerun) rerunIds.current[partId] ||= crypto.randomUUID()
      await playerRequest(`/uploads/${partId}/analysis/automatic`, 'POST', rerun ? { rerun_id: rerunIds.current[partId] } : undefined)
      delete rerunIds.current[partId]
      setGame(await playerRequest(`/games/${gameId}`))
    } catch (e) { setError(e.message) } finally { setBusy(false) }
  }
  // D34: a game is normally one video. Part wording is kept for a game still stored as separate parts.
  const videos = game ? withoutReplacedRetries(game.parts.map(part => ({ ...mapUpload(part), gameId }))) : []
  const gameParts = gamePartsFor(videos, gameId)
  const single = videos.length === 1
  const isPart = part => gameParts.length > 1 && gameParts.includes(part)
  // Auto-numbered names already end in " - Part N", so the prefix is added only when the name lacks it.
  const partTitle = part => part.name.endsWith(` - Part ${partNumber(part, gameParts)}`) ? part.name : `Part ${partNumber(part, gameParts)}: ${part.name}`
  const videoCount = gameParts.length > 1 ? ` · Game in ${gameParts.length} parts` : videos.length > 1 ? ` · ${videos.length} videos` : ''
  return <Stack spacing={2} sx={{ maxWidth: 1000, mx: 'auto', py: 2 }}>
    {error && <Alert severity="error">{error}</Alert>}
    <Typography variant="h4">{single ? 'Game details' : game?.name || 'Game'}</Typography>
    {game && <>
      <Typography color="text.secondary">{`${new Date(game.recorded_at).toLocaleDateString()}${videoCount}`}</Typography>
      {game.can_manage && <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6">Players selected for this game</Typography>
        {game.players.map(player => <Typography key={player.player_id}>{player.name} · {player.team || 'Team not specified'} · #{player.jersey || '?'} · {player.uniform || 'Uniform not specified'}</Typography>)}
        <Typography color="text.secondary" sx={{ mt: 1 }}>{game.players.length ? `Player matching uses this game’s appearance. Automatic analysis (beta) is ${game.auto_analyze ? 'on' : 'off'} for this game. Analysis status and results are shown below.` : 'No player selected. This game is organized for viewing only.'}</Typography>
      </Paper>}
      {videos.map(part => {
        const runs = latestRuns((game.analysis || []).filter(job => job.upload_id === part.id))
        const missing = game.players.filter(player => !hasGameAppearance(player))
        return <Paper key={part.id} variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="h6">{isPart(part) ? partTitle(part) : part.name}</Typography>
            <Typography color="text.secondary">{uploadStatusLabel(part)}</Typography>
            {game.can_manage && game.players.length > 0 && <>
              {runs.map(job => <Stack key={job.id} spacing={1}>
                <Typography role="status">{game.players.find(p => p.player_id === job.player_id)?.name}: {job.phase}</Typography>
                {['queued', 'launching', 'running'].includes(job.status) && <LinearProgress variant={job.status === 'queued' ? 'indeterminate' : 'determinate'} value={job.progress} />}
                {job.error && <Alert severity="warning">{job.error}</Alert>}
              </Stack>)}
              {part.status !== 'ready' && game.auto_analyze && <Typography>Player tracking and AI analysis (beta) will start when video processing finishes.</Typography>}
              {part.status === 'ready' && !runs.length && <Button disabled={busy || !game.players.some(hasGameAppearance)} onClick={() => startAnalysis(part.id)}>Start player tracking and analysis (beta)</Button>}
              {part.status === 'ready' && canRerun(runs, game.players) && <>
                <Button disabled={busy} onClick={() => startAnalysis(part.id, true)}>Re-run analysis</Button>
                <Typography variant="body2" color="text.secondary">Re-running analyzes the video you already uploaded; nothing is uploaded or transcoded again. Saving a clip from the new run updates the earlier AI highlight for that moment instead of adding a duplicate.</Typography>
              </>}
              {part.status === 'ready' && missing.length > 0 && <Alert severity="info">
                {missing.map(player => player.name).join(', ')}: this game’s jersey number or uniform is missing, so analysis started here cannot match {missing.length === 1 ? 'this player' : 'these players'}. Enter both in <RouterLink to={`/upload/${part.id}?analysis=1`}>analysis, clips and corrections</RouterLink> and start the analysis there.
              </Alert>}
              {part.status === 'ready' && <Button component={RouterLink} to={`/upload/${part.id}?analysis=1`}>View analysis, clips and corrections</Button>}
            </>}
            <Button component={RouterLink} to={`/upload/${part.id}`}>{single && isGameVideo(part) ? 'Open game video' : isPart(part) ? `Open part ${partNumber(part, gameParts)}` : 'Open video'}</Button>
          </Stack>
        </Paper>
      })}
      {!videos.length && <Typography>The game video will appear here once its upload starts.</Typography>}
    </>}
    <Button component={RouterLink} to="/uploads">Back to uploads</Button>
  </Stack>
}
